import { Component, OnInit, DestroyRef, inject } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { UnespCoreMessageService } from 'src/libs/unesp-core'
import { ComissaoJulgadoraService } from '../comissao-julgadora-service'
import { MatDialog } from '@angular/material/dialog'
import { ArquivoViewerDialogComponent } from '../../arquivo-viewer-dialog/arquivo-viewer-dialog.component'
import { DownloadService } from 'src/app/services/download.service'
import { firstValueFrom } from 'rxjs'
import { environment } from 'src/environments/environment'

interface DocumentoComplementar {
  token: string
  grupo: string
  tamanhoArquivo: number
  inscricaoId: number | null
  nomeCandidato: string | null
}

interface DocumentosConcursoResponse {
  anonimo: boolean
  candidatos: DocumentoComplementar[][]
}

@Component({
  selector: 'app-visualizar-documentos-concurso',
  templateUrl: './visualizar-documentos-concurso.component.html',
  styleUrls: ['./visualizar-documentos-concurso.component.scss'],
})
export class VisualizarDocumentosConcursoComponent implements OnInit {
  private destroyRef = inject(DestroyRef)

  concursoId!: number
  anonimo: boolean = true
  candidatos: DocumentoComplementar[][] = []
  candidatosMemorial: DocumentoComplementar[][] = []
  candidatosPlano: DocumentoComplementar[][] = []
  carregando: boolean = true
  downloadingDocuments: Set<string> = new Set()
  downloadProgress: Map<string, number> = new Map()

  constructor(
    private readonly route: ActivatedRoute,
    private readonly unespCoreMessageService: UnespCoreMessageService,
    private readonly comissaoJulgadoraService: ComissaoJulgadoraService,
    private readonly dialog: MatDialog,
    private readonly downloadService: DownloadService
  ) {}

  ngOnInit(): void {
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.concursoId = Number(params['concursoId'])
      this.carregarDocumentos()
    })
  }

  private carregarDocumentos(): void {
    this.comissaoJulgadoraService.listarDocumentosConcurso(this.concursoId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: DocumentosConcursoResponse) => {
          this.anonimo = response.anonimo
          this.candidatos = response.candidatos
          this.separarAgrupamentos()
          this.carregando = false
        },
        error: err => {
          this.unespCoreMessageService.showMessageError(
            err?.error?.message ?? 'Erro ao carregar documentos.'
          )
          this.carregando = false
        },
      })
  }

  private separarAgrupamentos(): void {
    this.candidatosMemorial = []
    this.candidatosPlano = []

    for (const docs of this.candidatos) {
      const memorial = docs.filter(d => !d.grupo.toLowerCase().includes('plano de trabalho'))
      const plano = docs.filter(d => d.grupo.toLowerCase().includes('plano de trabalho'))

      if (memorial.length > 0) {
        this.candidatosMemorial.push(memorial)
      }
      if (plano.length > 0) {
        this.candidatosPlano.push(plano)
      }
    }
  }

  getCandidatoLabel(index: number, docs: DocumentoComplementar[]): string {
    if (this.anonimo) {
      return `Candidato ${index + 1}`
    }
    const doc = docs[0]
    return doc.nomeCandidato
      ? `${doc.nomeCandidato} (Inscrição #${doc.inscricaoId})`
      : `Candidato ${index + 1}`
  }

  getMemorial(docs: DocumentoComplementar[]): DocumentoComplementar | undefined {
    return docs.find(d => d.grupo.toLowerCase().includes('memorial') && !d.grupo.toLowerCase().includes('comprovantes'))
  }

  getComprovantes(docs: DocumentoComplementar[]): DocumentoComplementar | undefined {
    return docs.find(d => d.grupo.toLowerCase().includes('comprovantes'))
  }

  async visualizarDocumento(doc: DocumentoComplementar, candidatoIndex: number, docIndex: number): Promise<void> {
    if (this.downloadingDocuments.has(doc.token)) return

    this.downloadingDocuments.add(doc.token)
    this.downloadProgress.set(doc.token, 0)

    try {
      const usarDownloadChunks = !!environment.usarDownloadChunks

      const response = await firstValueFrom(
        this.comissaoJulgadoraService.downloadDocumentoAnonimo(this.concursoId, doc.token, usarDownloadChunks)
      )

      const candidatoLabel = this.anonimo ? `Candidato ${candidatoIndex + 1}` : doc.nomeCandidato ?? `Candidato ${candidatoIndex + 1}`
      const titulo = `${candidatoLabel} - ${doc.grupo}`

      if (usarDownloadChunks) {
        const url = await this.downloadService.getObjectURLViaChunks(response, progress => {
          this.downloadProgress.set(doc.token, progress)
        })
        this.dialog.open(ArquivoViewerDialogComponent, {
          data: { tituloDoArquivo: titulo, urlDoArquivo: url },
          width: '98vw',
          height: '96vh',
          maxWidth: '100vw',
          panelClass: 'fullheight-dialog',
          autoFocus: false,
        })
      } else {
        const url = URL.createObjectURL(response)
        this.dialog.open(ArquivoViewerDialogComponent, {
          data: { tituloDoArquivo: titulo, urlDoArquivo: url },
          width: '98vw',
          height: '96vh',
          maxWidth: '100vw',
          panelClass: 'fullheight-dialog',
          autoFocus: false,
        })
      }
    } catch (error) {
      this.unespCoreMessageService.showMessageError('Erro ao carregar documento. Tente recarregar a página.')
    } finally {
      this.downloadingDocuments.delete(doc.token)
      this.downloadProgress.delete(doc.token)
    }
  }

  isDownloading(doc: DocumentoComplementar): boolean {
    return this.downloadingDocuments.has(doc.token)
  }

  getDownloadProgress(doc: DocumentoComplementar): number {
    return this.downloadProgress.get(doc.token) || 0
  }

  formatarTamanho(bytes: number | null): string {
    if (!bytes) return ''
    if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    if (bytes >= 1024) return (bytes / 1024).toFixed(0) + ' KB'
    return bytes + ' B'
  }

  voltar(): void {
    window.close()
  }
}
